/**
 * ActionAssigneeMultiSelector - Multi-select assignee picker.
 *
 * Thin wrapper around Combobox that:
 * - Fetches organization users if not provided via props
 * - Converts users to dropdown options with name labels and email values
 * - Validates freetext entries as email addresses
 * - Generates display names from email addresses
 */

import { useEffect, useState, useMemo, useId, useCallback } from 'react';
import Combobox, { type ComboboxOption, extractNameFromEmail } from 'src/components/ui/Combobox';
import { apiGet } from '@/lib/api/axios';
import UserAvatar from '@/components/ui/UserAvatar';
import { isValidEmail } from '@/lib/validation/email';

interface OrganizationUser {
  user_id: number;
  email: string;
  firstname?: string | null;
  surname?: string | null;
}

interface ActionAssigneeMultiSelectorProps {
  /** Currently selected email addresses */
  value: string[];
  /** Callback when selected emails change */
  onChange: (emails: string[]) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Label for the field */
  label?: string;
  /** Custom class name */
  className?: string;
  /** Optional class for the field label */
  labelClassName?: string;
  /** aria-label for the input */
  ariaLabel?: string;
  /** ID for the input element */
  id?: string;
  /** Optional users to use for the dropdown. If provided, skips fetching /organisations/users */
  users?: OrganizationUser[];
  /** Optional callback fired whenever the dropdown is opened */
  onOpen?: () => void;
}

export function ActionAssigneeMultiSelector({
  value,
  onChange,
  placeholder = 'Select assignees...',
  disabled = false,
  label,
  className,
  labelClassName,
  ariaLabel,
  id,
  users: propUsers,
  onOpen
}: ActionAssigneeMultiSelectorProps) {
  const [fetchedUsers, setFetchedUsers] = useState<OrganizationUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const generatedId = useId();
  const inputId = id || generatedId;

  const organizationUsers = propUsers ?? fetchedUsers;

  // Fetch organization users on mount only if no users prop provided
  useEffect(() => {
    if (propUsers !== undefined) return;
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const response = await apiGet<OrganizationUser[]>('/tenant-management/members', {
          meta: { globalError: false, context: 'action-assignee-multi-selector' }
        });
        setFetchedUsers(response.data);
      } catch (error) {
        console.error('Failed to fetch organization users:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, [propUsers]);

  // Convert organization users to dropdown options
  const userOptions: ComboboxOption[] = useMemo(
    () =>
      organizationUsers.map((user) => ({
        id: user.user_id,
        label: user.firstname && user.surname ? `${user.firstname} ${user.surname}` : extractNameFromEmail(user.email),
        value: user.email,
        avatar: (
          <UserAvatar
            firstname={user.firstname}
            surname={user.surname}
            email={user.email}
            size="xs"
            showTooltip={false}
          />
        )
      })),
    [organizationUsers]
  );

  const generateLabelFromValue = useCallback(
    (emailValue: string): string => {
      const user = organizationUsers.find((u) => u.email.toLowerCase() === emailValue.toLowerCase());
      if (user && user.firstname && user.surname) {
        return `${user.firstname} ${user.surname}`;
      }
      return extractNameFromEmail(emailValue);
    },
    [organizationUsers]
  );

  return (
    <div className={className}>
      {label && (
        <label htmlFor={inputId} className={labelClassName ?? 'block text-sm font-medium text-gray-700 mb-1'}>
          {label}
        </label>
      )}
      <Combobox
        allowCreate
        multiple
        id={inputId}
        value={value}
        onChange={onChange}
        options={userOptions}
        placeholder={isLoading ? 'Loading users...' : placeholder}
        disabled={disabled || isLoading}
        validateValue={isValidEmail}
        ariaLabel={ariaLabel || label}
        generateLabelFromValue={generateLabelFromValue}
        onOpen={onOpen}
      />
    </div>
  );
}

export default ActionAssigneeMultiSelector;
